import type {
  ArenaDisponivel,
  Login,
  RedefinicaoDeSenha,
  RespostaDeLogin,
  SessaoAberta,
} from '@arena/shared/schemas';
import type { Acao, Escopo, Projecao, Recurso } from '@arena/shared/permissoes';
import { api, tokens } from './cliente.js';

/**
 * As chamadas de identidade.
 *
 * Os tipos vêm de `@arena/shared` — os MESMOS que o backend usa para
 * responder. Não há um `interface LoginResponse` duplicado aqui que
 * possa divergir na próxima mudança de contrato.
 */

/** O que `GET /auth/eu` devolve: quem sou, onde, com quais permissões. */
export interface Eu {
  readonly usuario: { id: string; nome: string; email: string };
  readonly arenaId: string;
  readonly perfil: { id: string; chave: string };
  readonly permissoes: Record<
    Recurso,
    {
      nivel: string;
      rotulo: string;
      acoes: readonly Acao[];
      escopo: Escopo;
      projecao: Projecao;
    }
  >;
}

/** `GET /auth/2fa` — o que o card Segurança e a tela pessoal mostram. */
export interface SituacaoDoSegundoFator {
  readonly ativo: boolean;
  readonly codigosRestantes: number;
}

/** `POST /auth/2fa` — o segredo e os códigos de recuperação, uma vez só. */
export interface SegredoDoSegundoFator {
  readonly segredo: string;
  readonly url: string;
  readonly recuperacao: readonly string[];
}

export const authApi = {
  entrar(dados: Login): Promise<RespostaDeLogin> {
    return api<RespostaDeLogin>('/auth/login', { metodo: 'POST', corpo: dados, publica: true });
  },

  /** Segunda etapa: o código do app autenticador ou um de recuperação. */
  comTotp(desafio: string, codigo: string): Promise<RespostaDeLogin> {
    return api<RespostaDeLogin>('/auth/login/totp', {
      metodo: 'POST',
      corpo: { desafio, codigo },
      publica: true,
    });
  },

  /** Terceira etapa: qual arena, para quem tem mais de uma membership. */
  comArena(desafio: string, arenaId: string): Promise<SessaoAberta> {
    return api<SessaoAberta>('/auth/login/arena', {
      metodo: 'POST',
      corpo: { desafio, arenaId },
      publica: true,
    });
  },

  /**
   * "Esqueci a senha" — com o código que o Dono gerou e entregou.
   *
   * `publica`: quem chama não tem sessão, e é por isso que a rota existe.
   * Todo "não" vem com a mesma frase do servidor — e-mail que não existe,
   * código errado, expirado ou já usado. Não há o que distinguir aqui.
   */
  redefinirSenha(dados: RedefinicaoDeSenha): Promise<void> {
    return api<void>('/auth/senha/redefinir', { metodo: 'POST', corpo: dados, publica: true });
  },

  eu(): Promise<Eu> {
    return api<Eu>('/auth/eu');
  },

  arenas(): Promise<ArenaDisponivel[]> {
    return api<ArenaDisponivel[]>('/auth/arenas');
  },

  /**
   * Trocar de arena consome o refresh atual: a família antiga é
   * encerrada e uma nova nasce apontando para a arena escolhida.
   */
  trocarArena(arenaId: string): Promise<SessaoAberta> {
    const { refresh } = tokens.ler();
    return api<SessaoAberta>('/auth/arena', {
      metodo: 'POST',
      corpo: { arenaId, refresh },
    });
  },

  async sair(): Promise<void> {
    const { refresh } = tokens.ler();
    if (refresh) {
      // Se o servidor não responder, o logout local acontece do mesmo
      // jeito: ficar preso numa sessão que a pessoa pediu para encerrar
      // é pior que uma sessão órfã que expira sozinha.
      await api<void>('/auth/logout', { metodo: 'POST', corpo: { refresh } }).catch(() => {});
    }
    tokens.limpar();
  },

  // -------------------------------------------------------------------
  // Segundo fator — o card Segurança de #v-acessos, "A ativar"
  // -------------------------------------------------------------------

  situacaoTotp(): Promise<SituacaoDoSegundoFator> {
    return api<SituacaoDoSegundoFator>('/auth/2fa');
  },

  /** Gera segredo e códigos de recuperação. NÃO liga a exigência ainda. */
  iniciarTotp(): Promise<SegredoDoSegundoFator> {
    return api<SegredoDoSegundoFator>('/auth/2fa', { metodo: 'POST' });
  },

  /** Confirma com um código do app e liga a exigência. */
  ativarTotp(codigo: string): Promise<void> {
    return api<void>('/auth/2fa/ativar', { metodo: 'POST', corpo: { codigo } });
  },

  /** Exige senha E código — desativar com sessão sequestrada seria fácil demais. */
  desativarTotp(senha: string, codigo: string): Promise<void> {
    return api<void>('/auth/2fa/desativar', { metodo: 'POST', corpo: { senha, codigo } });
  },
};
